import React from 'react';

import { getGapDateRangesFromPeriodSelection, locale } from '@trqk/utils';

const {
    enUS: {
        MANAGE_GAP_ANALYSIS: {
            STEPS: {
                SET_UP: { DATA_LABELS },
            },
        },
    },
} = locale;

const warningClasses = 'text-quinary-500 font-semibold';

interface GetGapDataSourceLabelsInput {
    /**
     * Formatting options to apply to date values
     */
    localeStringOptions: Intl.DateTimeFormatOptions;
    /**
     * Current value of `context.selectedPeriod`
     */
    selectedPeriod: Maybe<PeriodSelectionWithDetectionFilename>;
    /**
     * The current {@link TrqkGap} being analyzed
     */
    trqkGap: Maybe<TrqkGap>;
}

export const getGapDataSourceLabels = ({
    localeStringOptions,
    selectedPeriod,
    trqkGap,
}: GetGapDataSourceLabelsInput): GapDataSourceLabels => {
    const statement_period_label =
        selectedPeriod?.pro_statement_period_label?.statement_period_label ??
        trqkGap?.performance_period ??
        null;
    const pro =
        selectedPeriod?.pro_statement_period_label?.pro ?? trqkGap?.pro ?? null;
    const dateRanges = getGapDateRangesFromPeriodSelection(selectedPeriod);

    const result: GapDataSourceLabels = {
        detections: {
            missingLabel: (
                <p className={warningClasses}>
                    {DATA_LABELS.MISSING_DETECTIONS}
                </p>
            ),
            dateRange: null,
            description: null,
            filename: trqkGap?.detection_batch?.filename ?? null,
        },
        performances: {
            missingLabel: (
                <p className={warningClasses}>
                    {DATA_LABELS.MISSING_PERFORMANCES}
                </p>
            ),
            dateRange: null,
            description: null,
            pro,
            statementPeriodLabel: statement_period_label,
        },
    };

    if (
        dateRanges.performancePeriod.start &&
        dateRanges.performancePeriod.end
    ) {
        // If we have performance data, use it to generate result.performances.dateRange
        const performancesStartLabel =
            dateRanges.performancePeriod.start.toLocaleString(
                'en',
                localeStringOptions,
            );
        const performancesEndLabel =
            dateRanges.performancePeriod.end.toLocaleString(
                'en',
                localeStringOptions,
            );

        result.performances.dateRange = `${performancesStartLabel} ${DATA_LABELS.TO} ${performancesEndLabel}`;
    }

    if (dateRanges.detections.end && dateRanges.detections.start) {
        // If we have detection data with an actual start / end date, add it
        const detectionsStartLabel = dateRanges.detections.start.toLocaleString(
            'en',
            localeStringOptions,
        );
        const detectionsEndLabel = dateRanges.detections.end.toLocaleString(
            'en',
            localeStringOptions,
        );

        result.detections.dateRange = `${detectionsStartLabel} ${DATA_LABELS.TO} ${detectionsEndLabel}`;
    } else if (result.performances.dateRange !== null) {
        // Otherwise, if possible, use the result.performances.dateRange to create
        // result.detections.missingLabel to show that the full range of detections are missing

        result.detections.missingLabel = (
            <p
                className={warningClasses}
            >{`${DATA_LABELS.MISSING_DETECTIONS_FROM} ${result.performances.dateRange}`}</p>
        );
    }

    if (statement_period_label && pro) {
        result.performances.description = `${pro} ${statement_period_label} ${DATA_LABELS.STATEMENT}`;
        result.performances.missingLabel = (
            <p
                className={warningClasses}
            >{`${DATA_LABELS.MISSING} ${pro} ${statement_period_label} ${DATA_LABELS.STATEMENT}`}</p>
        );
    }

    return result;
};
